import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { BottomNav } from './BottomNav';
import { PageTransition } from './PageTransition';
import { Toaster } from 'sonner';

export const ProtectedLayout = () => {
    const { user, profile, loading, profileLoading } = useAuth();
    const location = useLocation();

    // 0. Path Definitions (Moved to top for scope availability)
    const path = location.pathname;
    const isBloqueadoPage = path === '/bloqueado';
    const isTrocarSenhaPage = path === '/trocar-senha';
    const isRedefinirSenhaPage = path === '/redefinir-senha';
    const isIntroPage = path === '/intro-video' || path === '/onboarding';
    const isAnamnesePage = path === '/anamnese';

    // GLOBAL ANAMNESE CHECK
    const {
        data: hasAnamnese,
        isLoading: isCheckingAnamnese
    } = useQuery({
        queryKey: ['anamnese-check-global', user?.id],
        queryFn: async () => {
            if (!user) return false;

            const { data, error } = await supabase.rpc('verificar_anamnese', {
                p_user_id: user.id
            });

            if (error) {
                console.error('❌ Erro crítico checando anamnese:', error);
                throw error;
            }

            return data;
        },
        enabled: !!user && !profileLoading, // Only check after profile is loaded
        retry: 3,
        staleTime: Infinity,
        refetchOnWindowFocus: false
    });

    // 1. Auth Loading
    // wait for session AND profile (if user exists)
    if (loading || (user && profileLoading && !profile)) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center space-y-4 bg-background">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <p className="text-muted-foreground animate-pulse">Carregando...</p>
            </div>
        );
    }

    // 2. Auth Check
    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // 3. First Access Check (PRIORITY: Before Anamnese Loading)
    if (profile?.primeiro_login) {
        if (!isTrocarSenhaPage) {
            return <Navigate to="/trocar-senha" replace />;
        }
        return (
            <div className="min-h-screen bg-background pb-16 md:pb-0">
                <PageTransition>
                    <Outlet />
                </PageTransition>
                <Toaster richColors position="top-center" closeButton />
            </div>
        );
    }

    // 4. Anamnese Loading (Only runs if NOT first login AND NOT on trocar-senha)
    // Não desmontar TrocarSenha enquanto a anamnese carrega para evitar tela branca
    if (isCheckingAnamnese && !isTrocarSenhaPage) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center space-y-4 bg-background">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <p className="text-muted-foreground animate-pulse">Verificando histórico...</p>
            </div>
        );
    }

    // C. Verificações de Anamnese (Only if not blocked and not first login)

    // C1. User completed Anamnese
    if (hasAnamnese) {
        // If trying to access onboarding/intro or anamnese page again
        // (Assuming you can view anamnese in profile, but /anamnese route is for creation?)
        // Usually we redirect away from flow pages if done.
        if (isIntroPage || isAnamnesePage) {
            return <Navigate to="/" replace />;
        }

        // Prevent accessing blocked/password pages if resolved
        // Prevent accessing blocked/password pages if resolved (except RedefinirSenha which is explicit intent)
        if (isBloqueadoPage || isTrocarSenhaPage) {
            return <Navigate to="/" replace />;
        }
    }

    // C2. User MISSING Anamnese (Mandatory Flow)
    if (!hasAnamnese) {
        const isAllowedPhase = isIntroPage || isAnamnesePage || isRedefinirSenhaPage || isTrocarSenhaPage;

        if (!isAllowedPhase) {
            // Force redirection to Anamnese (Skipping VideoOnboarding for now)
            return <Navigate to="/anamnese" replace />;
        }
    }

    // Default Render
    return (
        <div className="min-h-screen bg-background pb-16 md:pb-0">
            <PageTransition>
                <Outlet />
            </PageTransition>

            {/* BottomNav only if Anamnese is done */}
            {hasAnamnese && <BottomNav />}
            <Toaster richColors position="top-center" closeButton />
        </div>
    );
};
